/**
 * LoginPage Component
 * Handles user authentication with Cognito.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, AlertCircle, LogIn, ArrowLeft } from 'lucide-react';
import { PageLayout } from '@/components';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button, Input, Card, IconContainer } from '@/components/ui';
import { errorMessages } from '@/lib/errors';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { login, isLoading, error: authError, isAuthenticated } = useAuthContext();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get the redirect path from location state, default to /add
  const from = (location.state as { from?: string })?.from || '/add';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessages.login(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [email, password, login, navigate, from]);

  const displayError = error || authError;

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="flex items-center justify-center px-4 pt-20 pb-12 sm:pt-24">
        <div className="max-w-md w-full">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <IconContainer variant="primary" size="xl" className="mx-auto mb-4">
              <LogIn className="w-8 h-8" />
            </IconContainer>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              {t('login.title')}
            </h1>
            <p className="text-slate-600">
              {t('login.subtitle')}
            </p>
          </div>

          {/* Error Display */}
          {displayError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{displayError}</p>
            </div>
          )}

          {/* Login Form */}
          <Card variant="form" padding="xl">
            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('login.emailLabel')}
                </label>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  leftIcon={<Mail className="w-5 h-5" />}
                  inputSize="lg"
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('login.passwordLabel')}
                </label>
                <Input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  leftIcon={<Lock className="w-5 h-5" />}
                  inputSize="lg"
                  placeholder={t('login.passwordPlaceholder')}
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Help Links */}
            <div className="flex justify-between mt-2">
              <button
                type="button"
                onClick={() => navigate('/resend-verification')}
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                {t('login.didntReceiveVerification')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
              >
                {t('login.forgotPassword')}
              </button>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="gradient-blue"
              size="lg"
              shape="rounded"
              fullWidth
              className="mt-6"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('login.signingIn')}
                </span>
              ) : (
                t('login.signIn')
              )}
            </Button>
            </form>
          </Card>

          {/* Sign up link */}
          <p className="mt-6 text-center text-slate-600">
            {t('login.noAccount')}{' '}
            <button
              onClick={() => navigate('/signup')}
              className="text-blue-600 font-medium hover:text-blue-700 transition-colors"
            >
              {t('login.signUp')}
            </button>
          </p>
        </div>
      </div>
    </PageLayout>
  );
};

export default LoginPage;
